import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Clock, MessageCircle, Mail, FileText, User, Star, Calendar, Trash2, Edit, CheckCircle, XCircle } from 'lucide-react'
import { db } from '../../firebase'
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'
import { getTimeAgo } from '../../utils/helpers.jsx'

const ActivityLog = ({ maxItems = 10 }) => {
  const [activities, setActivities] = useState([])
  const [filter, setFilter] = useState('all') // all, chat, contact, review, consultation, application

  useEffect(() => {
    // Listen to multiple collections for real-time activity
    const unsubscribers = []

    // Chat Messages
    const chatQuery = query(collection(db, 'chatMessages'), orderBy('timestamp', 'desc'), limit(20))
    unsubscribers.push(onSnapshot(chatQuery, (snapshot) => {
      const chatActivities = snapshot.docs.map(doc => ({
        id: `chat-${doc.id}`,
        type: 'chat',
        action: 'Mesazh i ri',
        description: `nga ${doc.data().senderName || 'User'}`,
        icon: MessageCircle,
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/20',
        timestamp: doc.data().timestamp?.toDate() || new Date(),
        details: doc.data().text?.substring(0, 50) + '...'
      }))
      updateActivities(chatActivities, 'chat')
    }))

    // Contact Submissions
    const contactQuery = query(collection(db, 'contactSubmissions'), orderBy('createdAt', 'desc'), limit(20))
    unsubscribers.push(onSnapshot(contactQuery, (snapshot) => {
      const contactActivities = snapshot.docs.map(doc => ({
        id: `contact-${doc.id}`,
        type: 'contact',
        action: 'Formë kontakti e re',
        description: `nga ${doc.data().name}`,
        icon: Mail,
        color: 'text-green-400',
        bgColor: 'bg-green-500/20',
        timestamp: doc.data().createdAt?.toDate() || new Date(),
        details: doc.data().email
      }))
      updateActivities(contactActivities, 'contact')
    }))

    // Reviews
    const reviewsQuery = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'), limit(20))
    unsubscribers.push(onSnapshot(reviewsQuery, (snapshot) => {
      const reviewActivities = snapshot.docs.map(doc => {
        const data = doc.data()
        let action = 'Review e re'
        let icon = Star
        
        if (data.status === 'approved') {
          action = 'Review u aprovua'
          icon = CheckCircle
        } else if (data.status === 'denied') {
          action = 'Review u refuzua'
          icon = XCircle
        }
        
        return {
          id: `review-${doc.id}`,
          type: 'review',
          action,
          description: `nga ${data.name}`,
          icon,
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/20',
          timestamp: data.createdAt?.toDate() || new Date(),
          details: `${data.rating} ⭐`
        }
      })
      updateActivities(reviewActivities, 'review')
    }))

    // Consultations
    const consultQuery = query(collection(db, 'consultations'), orderBy('createdAt', 'desc'), limit(20))
    unsubscribers.push(onSnapshot(consultQuery, (snapshot) => {
      const consultActivities = snapshot.docs.map(doc => ({
        id: `consult-${doc.id}`,
        type: 'consultation',
        action: 'Kërkesë për konsultim',
        description: `nga ${doc.data().name}`,
        icon: Calendar,
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/20',
        timestamp: doc.data().createdAt?.toDate() || new Date(),
        details: doc.data().service
      }))
      updateActivities(consultActivities, 'consultation')
    }))

    // Applications
    const appQuery = query(collection(db, 'applications'), orderBy('createdAt', 'desc'), limit(20))
    unsubscribers.push(onSnapshot(appQuery, (snapshot) => {
      const appActivities = snapshot.docs.map(doc => ({
        id: `app-${doc.id}`,
        type: 'application',
        action: 'Aplikim i ri për projekt',
        description: `nga ${doc.data().name}`,
        icon: FileText,
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/20',
        timestamp: doc.data().createdAt?.toDate() || new Date(),
        details: doc.data().projectType
      }))
      updateActivities(appActivities, 'application')
    }))

    return () => unsubscribers.forEach(unsub => unsub())
  }, [])

  const updateActivities = (newActivities, type) => {
    setActivities(prev => {
      // Remove old activities of this type
      const filtered = prev.filter(a => a.type !== type)
      // Add new activities and sort by timestamp
      const combined = [...filtered, ...newActivities]
      combined.sort((a, b) => b.timestamp - a.timestamp)
      return combined.slice(0, 100) // Keep only latest 100
    })
  }

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities.filter(a => a.type === filter)

  const displayActivities = filteredActivities.slice(0, maxItems)

  const getActivityIcon = (activity) => {
    const Icon = activity.icon
    return (
      <div className={`w-10 h-10 rounded-full ${activity.bgColor} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${activity.color}`} />
      </div>
    )
  }

  return (
    <div className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-display font-bold text-gold-400 flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          Aktiviteti i Fundit
        </h3>
        
        {/* Filter Buttons */}
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'Të Gjitha', icon: Clock },
            { key: 'chat', label: 'Chat', icon: MessageCircle },
            { key: 'contact', label: 'Kontakt', icon: Mail },
            { key: 'review', label: 'Review', icon: Star },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1 ${
                filter === key
                  ? 'bg-gradient-gold text-white'
                  : 'bg-dark/30 text-beige-300 hover:bg-dark/50'
              }`}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        <AnimatePresence>
          {displayActivities.length > 0 ? (
            displayActivities.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start space-x-3 p-3 bg-dark/30 hover:bg-dark/50 rounded-xl transition-all cursor-pointer group"
              >
                {getActivityIcon(activity)}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-cream font-medium text-sm group-hover:text-gold-400 transition-colors">
                        {activity.action}
                      </p>
                      <p className="text-beige-400 text-xs mt-0.5">
                        {activity.description}
                      </p>
                      {activity.details && (
                        <p className="text-beige-500 text-xs mt-1 truncate">
                          {activity.details}
                        </p>
                      )}
                    </div>
                    <span className="text-beige-500 text-xs whitespace-nowrap ml-2">
                      {getTimeAgo(activity.timestamp)}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8"
            >
              <Clock className="w-12 h-12 text-beige-400 mx-auto mb-3 opacity-50" />
              <p className="text-beige-300 text-sm">
                {filter === 'all' ? 'Nuk ka aktivitet ende' : `Nuk ka ${filter} ende`}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {displayActivities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gold-500/10 text-center">
          <p className="text-beige-400 text-xs">
            Duke shfaqur {displayActivities.length} nga {filteredActivities.length} aktivitete
          </p>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(201, 169, 97, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(201, 169, 97, 0.5);
        }
      `}</style>
    </div>
  )
}

export default ActivityLog
